export type LensId = "ioc" | "maintenance" | "energy" | "mobility" | "xray";

// ── Base maps ──────────────────────────────────────────────────────────
// Mutually exclusive ground layer that everything else stacks on top of.
// Picking one swaps the bottom imagery: streets (Esri Gray Canvas rasters),
// topography (OpenTopoMap contours), aerial (Esri high-res satellite),
// night (VIIRS DayNightBand), or true-color (VIIRS NOAA-20). The picker
// lives at the very top of LayerPalette so the user resolves the "what
// am I looking at?" question first, then layers facts on top.
export type BaseMapId = "streets" | "topo" | "aerial" | "night" | "true-color";

export interface BaseMap {
  id: BaseMapId;
  label: string;
  describe: string;
  /** Tiny color chip on the picker button so users associate map style with hue. */
  swatch: string;
}

export const BASE_MAPS: BaseMap[] = [
  { id: "streets",    label: "Streets",    describe: "Esri Gray Canvas + OpenStreetMap streets and labels. Best for live traffic and operational overlays.", swatch: "#3F4756" },
  { id: "topo",       label: "Topo",       describe: "OpenTopoMap — contours, hillshade, terrain. Useful at regional zoom.",                       swatch: "#A3E635" },
  { id: "aerial",     label: "Aerial",     describe: "Esri World Imagery — high-resolution aerial / satellite photos. Best for ground-truth.",     swatch: "#60A5FA" },
  { id: "night",      label: "Night",      describe: "VIIRS DayNightBand — Earth at night. Bangkok shows up as a golden constellation. Satellite product: only sharp at city-wide zoom, soft at campus zoom.",            swatch: "#1F6E43" },
  { id: "true-color", label: "True color", describe: "VIIRS NOAA-20 true-color — daily corrected reflectance, clouds + ground. Satellite product: only sharp at city-wide zoom, soft at campus zoom.",              swatch: "#A5F3FC" },
];

export type LayerId =
  | "campus-boundary"
  | "campus-buildings"
  | "flood-gauges"
  | "bma-aq-stations"
  | "traffic-live"
  | "incidents-itic"
  | "incidents-city-reports"
  | "incidents-city-heatmap"
  | "cctv-cameras"
  | "cu-shuttle-routes"
  | "cu-shuttle-1"
  | "cu-shuttle-2"
  | "cu-shuttle-3"
  | "cu-shuttle-4"
  | "cu-shuttle-5"
  | "cu-shuttle-stops"
  | "cu-shuttle-vehicles"
  | "transit-stations"
  | "transit-lines"
  | "campus-gates"
  | "utility-electricity"
  | "utility-water"
  | "utility-drainage"
  | "campus-energy"
  | "campus-trees"
  | "campus-wood"
  | "parking-capacity"
  | "wifi-signal"
  | "building-occupancy"
  | "internal-traffic"
  | "fire-hose"
  | "trash-collection"
  // Pratumwan X-Ray — SimCity-styled subsurface + overhead infrastructure
  | "pratumwan-boundary"
  | "pratumwan-ground-plane"
  | "pratumwan-metro-tunnels"
  | "pratumwan-metro-stations"
  | "pratumwan-water"
  | "pratumwan-sewer"
  | "pratumwan-storm"
  | "pratumwan-mea-hv";

export interface Lens {
  id: LensId;
  label: string;
  /** Full name + 1-line explanation rendered as the button's hover tooltip. */
  describe: string;
  layers: LayerId[];
  /** Base map that snaps in when this lens is picked. Defaults to "streets". */
  baseMap?: BaseMapId;
  /** Sample / unfinished lenses — hide from the ops palette. */
  hideFromPalette?: boolean;
}

export const LENSES: Lens[] = [
  {
    id: "ioc",
    label: "IOC",
    describe: "Campus brief: shuttle buses, occupancy, internal traffic, incidents, air, flood gauges, CCTV and citizen reports on one street map.",
    baseMap: "streets",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "campus-gates",
      "bma-aq-stations",
      "transit-stations",
      "transit-lines",
      "traffic-live",
      "internal-traffic",
      "cu-shuttle-routes",
      "cu-shuttle-stops",
      "cu-shuttle-vehicles",
      "building-occupancy",
      "parking-capacity",
      "incidents-city-reports",
      "incidents-itic",
      "cctv-cameras",
      "flood-gauges",
    ],
  },
  {
    id: "maintenance",
    label: "MAINT",
    describe: "Predictive maintenance: fire-hose inspections, bin fill, parking pressure, citizen reports, traffic events and flood watch — work from the bottom issue roll.",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "campus-gates",
      "fire-hose",
      "trash-collection",
      "parking-capacity",
      "building-occupancy",
      "traffic-live",
      "incidents-city-reports",
      "incidents-city-heatmap",
      "incidents-itic",
      "flood-gauges",
    ],
  },
  {
    id: "energy",
    label: "EGY",
    describe: "Campus energy view. Aggregate load is modeled (scenario) until BEMS lands; Chamchuri 5 block is measured 2019.",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "campus-energy",
      "building-occupancy",
      "wifi-signal",
    ],
  },
  {
    id: "mobility",
    label: "MOB",
    describe: "Mobility operations: simulated POP Bus fleet on real road loops, live Longdo/iTIC road speeds and events, internal campus traffic, parking capacity, transit and CCTV.",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "campus-gates",
      "transit-lines",
      "transit-stations",
      "traffic-live",
      "internal-traffic",
      "incidents-itic",
      "cu-shuttle-1",
      "cu-shuttle-2",
      "cu-shuttle-3",
      "cu-shuttle-4",
      "cu-shuttle-5",
      "cu-shuttle-stops",
      "cu-shuttle-vehicles",
      "parking-capacity",
      "cctv-cameras",
    ],
  },
  {
    id: "xray",
    label: "X-RAY",
    describe: "Pathumwan underground — MRT Blue Line tunnel (−22 m), MWA mains, BMA sewers/storm, MEA cables under a faded ground plane.",
    baseMap: "streets",
    layers: [
      "campus-boundary",
      "campus-buildings",
      "pratumwan-boundary",
      "pratumwan-ground-plane",
      "pratumwan-metro-tunnels",
      "pratumwan-metro-stations",
      "pratumwan-water",
      "pratumwan-sewer",
      "pratumwan-storm",
      "utility-electricity",
      "utility-water",
      "utility-drainage",
      "transit-stations",
      "transit-lines",
    ],
  },
];

export type LayerGroup = "campus" | "operations" | "mobility" | "incidents" | "utility" | "environment" | "xray";

export const LAYER_GROUP_LABEL: Record<LayerGroup, string> = {
  campus:      "Campus",
  operations:  "Operations",
  mobility:    "Mobility",
  incidents:   "Incidents",
  utility:     "Utility",
  environment: "Environment",
  xray:        "Pratumwan X-Ray",
};

// ── Connector layers (ADR 0001 §1) ─────────────────────────────────────────
// Declarative data sources are not static LayerIds: the registry decides
// which exist. Their layer ids are "connector:<id>" and they render in one
// dynamic group after the static ones (see map/connectorLayer.ts).
export const CONNECTOR_GROUP_LABEL = "Connected sources";
export const CONNECTOR_GROUP_HINT =
  "Sources departments plugged in with a connector manifest — each row shows its own data tier";

export const ALL_LAYERS: {
  id: LayerId;
  label: string;
  swatch: string;
  group: LayerGroup;
  /** Hover tooltip text — what this layer shows and where the data comes from. */
  describe: string;
}[] = [
  // Campus
  { id: "campus-boundary",   label: "Campus boundary",         swatch: "#B7307A", group: "campus",
    describe: "Chulalongkorn University official perimeter from OSM (MultiPolygon, 8 sub-areas)." },
  { id: "campus-buildings",  label: "Campus buildings (OSM)",  swatch: "#B7307A", group: "campus",
    describe: "Campus building footprints, with height from OSM building:levels (or our heuristic). Click any building for name + levels; 3D mode extrudes them." },
  { id: "bma-aq-stations",   label: "AQ stations",             swatch: "#22D3EE", group: "campus",
    describe: "Live air-quality station dots — color-coded by PM2.5 reading (green→yellow→orange→red→purple). Pulls from BMA + AQICN (PCD, US-embassy, research monitors when AQICN_TOKEN is set)." },
  // Mobility
  { id: "traffic-live",      label: "Traffic speed — LIVE",    swatch: "#22C55E", group: "mobility",
    describe: "Live directional road speeds from the official Longdo/iTIC vector tile layer. Green = free flow, yellow = slow, red = heavy. Provider cadence is approximately 5 minutes; display-only, not stored." },
  { id: "cu-shuttle-routes", label: "CU Shuttle — all routes", swatch: "#EF4444", group: "mobility",
    describe: "All 5 CU POP Bus lines rendered together — free 100% electric shuttle, ~3 M passenger trips/year (Samyan Smart City data)." },
  { id: "cu-shuttle-1",      label: "Shuttle Line 1 (Sat)",    swatch: "#EF4444", group: "mobility",
    describe: "POP Bus Line 1 (red), 10 stops · 3.8 km: Sala Phra Kieo → Political Science → Patumwan Demonstration School → Veterinary Hospital → Chalerm Phao junction → Lido → Osot Sala (Pharmacy) → Architecture → Arts → Engineering. Mon–Fri and Saturday." },
  { id: "cu-shuttle-2",      label: "Shuttle Line 2 (Sat)",    swatch: "#38BDF8", group: "mobility",
    describe: "POP Bus Line 2 (blue), 15 stops · 5.6 km: Sala Phra Kieo → Science → Education → CU Demonstration School → Chamchuri 9 → Sports Centre → Dhamma Center → Witthayaniwet → Allied Health → BTS National Stadium → Sports Science → Sasin → Architecture → Arts → Engineering. Mon–Fri and Saturday." },
  { id: "cu-shuttle-3",      label: "Shuttle Line 3 (wkdy)",   swatch: "#34D399", group: "mobility",
    describe: "POP Bus Line 3 (green), 7 stops · 3.9 km: Sala Phra Kieo → Political Science → Medicine → Science → Architecture → Arts → Engineering. Weekdays only." },
  { id: "cu-shuttle-4",      label: "Shuttle Line 4 (wkdy)",   swatch: "#FBBF24", group: "mobility",
    describe: "POP Bus Line 4 (yellow), 12 stops · 8.3 km — the long loop: Sala Phra Kieo → Lido → Osot Sala → Mahamakut → Science → Education → U-Center → Samyan Mitrtown → Chamchuri 9 → Communication Arts → Arts → Engineering. Weekdays only." },
  { id: "cu-shuttle-5",      label: "Shuttle Line 5 (wkdy)",   swatch: "#A78BFA", group: "mobility",
    describe: "POP Bus Line 5 (purple), 8 stops · 4.7 km: Student dormitories → Dhamma Center → Education → Science → Sala Phra Kieo → Samyan Market → I'm Park → Rabiang Chamchuri. Weekdays only." },
  { id: "cu-shuttle-stops",  label: "CU Shuttle stops",        swatch: "#FBBF24", group: "mobility",
    describe: "47 named shuttle stops — Sala Phra Kieo, Osot Sala, Samyan Market, BTS National Stadium, etc. Hover for which lines call there." },
  { id: "cu-shuttle-vehicles", label: "CU Shuttle — buses (SIM)", swatch: "#22D3EE", group: "mobility",
    describe: "POP Bus fleet on the published timetable: Mon–Fri 07:00–19:00 all lines, Sat Lines 1–2, 4 buses per line at 20 km/h with a 60 s dwell at every stop, driving real road loops. SIMULATED (scenario tier) until the ViaBus GPS feed is wired — then this same layer shows live positions." },
  { id: "transit-stations",  label: "BTS / MRT stations",      swatch: "#38BDF8", group: "mobility",
    describe: "15 nearby rail stations: BTS National Stadium, Siam, Ratchadamri; MRT Sam Yan, Hua Lamphong, Si Lom, Wat Mangkon. Walking distance to campus gates." },
  { id: "transit-lines",     label: "BTS / MRT lines",         swatch: "#057B43", group: "mobility",
    describe: "Polyline tracks for BTS Sukhumvit + Silom, MRT Blue, and the Gold APM around the campus. Coloured per system per Bangkok's official line palette." },
  { id: "campus-gates",      label: "Campus gates / entrances", swatch: "#FBBF24", group: "campus",
    describe: "Every gate and entrance on the campus perimeter. Named ones — ประตูพญาไท 1/2/3, อังรีดูนังต์ 1/2, ประตูดำ — render larger; minor gates smaller." },
  { id: "cctv-cameras",      label: "CCTV cameras",            swatch: "#E5E7EB", group: "mobility",
    describe: "Public traffic cameras from Longdo (BMA + other operators) inside the campus bbox. Click for the live JPG/HLS stream." },
  // Operations — modeled from real campus geometry (scenario tier, see scripts/build-ops-mock.mjs)
  { id: "parking-capacity",   label: "Parking — capacity & fill", swatch: "#7A8497", group: "operations",
    describe: "74 lots and structures with modeled capacity (polygon area ÷ 25 m² per car, × storeys for structures; OSM tag kept where present) and a clock-driven occupancy model — weekday commute humps to ~85 % by mid-morning. Label = occupied / capacity. Scenario tier until gate counters or LPR arrive." },
  { id: "building-occupancy", label: "Building occupancy",       swatch: "#B7307A", group: "operations",
    describe: "339 buildings coloured by modeled people-in-building right now. Capacity = footprint × floors ÷ 12 m² per person; the live figure follows the academic calendar tempo and an hour-of-day curve. Scenario tier until Wi-Fi association counts or keycard data are wired." },
  { id: "internal-traffic",   label: "Internal traffic (campus)", swatch: "#22C55E", group: "operations",
    describe: "63 campus-internal road segments coloured by modeled speed: gate approaches (within 120 m of a gate) slow to a crawl 07:30–09:00 and 16:00–18:00, the interior stays free-flow. Scenario tier — the surrounding arterials use the live Longdo/iTIC layer." },
  { id: "wifi-signal",        label: "WiFi signal",              swatch: "#22D3EE", group: "operations",
    describe: "Per-building access-point estimate (2 per floor) and signal strength in dBm interpolated from the 15 real ChulaWiFi-2 survey points, plus a speed heatmap. Green = strong, red = weak. Scenario tier until the controller feed is wired." },
  { id: "fire-hose",          label: "Fire hose & hydrants",     swatch: "#A23A26", group: "operations",
    describe: "726 hose-reel cabinets (one per two storeys, on the facade) and 48 gate hydrants with residual pressure and last-inspection date. Red = overdue, amber = due, green = ok. Seeded ~8 % due, ~2 % overdue — the shape of a real campus safety audit. Scenario tier." },
  { id: "trash-collection",   label: "Trash collection points",  swatch: "#6F6C63", group: "operations",
    describe: "174 bins where people gather — a litter + recycling pair at every gate, general + food-waste at markets and canteens, one per building over four floors. Fill level rises through the day with pickups at 06:30, 13:00 and 19:30. Scenario tier until bin sensors arrive." },
  // Incidents
  { id: "incidents-itic",    label: "iTIC traffic events",     swatch: "#A23A26", group: "incidents",
    describe: "Live iTIC / Longdo traffic events: accidents, closures, breakdowns, construction. Filtered to campus bbox." },
  { id: "incidents-city-reports", label: "City Reporter — dots",   swatch: "#A78BFA", group: "incidents",
    describe: "Live citizen complaints (LINE / Telegram / Traffy): road damage, lighting, drainage, waste, etc. Color = category, size = severity. Click any dot for the full audit trail." },
  { id: "incidents-city-heatmap", label: "City Reporter — hotspots", swatch: "#F87171", group: "incidents",
    describe: "Severity-weighted density heatmap of open city reports. Highlights recurring problem zones — a single critical report still shows as a hot spot. Resolved reports are excluded." },
  // Environment / Risk
  { id: "flood-gauges", label: "Flood gauges (FloodDash)", swatch: "#38BDF8", group: "environment",
    describe: "Live HII gauge stations nearest campus via the FloodDash sister system (:8340): water-level stations colored by situation level (green normal → amber high → red overbank), rain gauges in blue scaled by 24h accumulation. Hover any dot for the reading." },
  // Underground utilities (electricity / water / drainage) + WiFi survey
  { id: "utility-electricity", label: "Electricity (MEA · approx)", swatch: "#1F6E43", group: "utility",
    describe: "REAL: Pathumwan + Samyan substations from the CU-MEA agreement, plus solar PV cluster nodes (20 MW microgrid target) + BESS. APPROX: HV 115 kV and MV 22 kV feeders following the road network." },
  { id: "utility-water",     label: "Water mains (MWA · approx)",   swatch: "#38BDF8", group: "utility",
    describe: "Approximated MWA potable supply: 400 mm perimeter loop + 200/250/300 mm laterals. Drops to PMCU as-built when supplied." },
  { id: "utility-drainage",  label: "Storm drainage (approx)",      swatch: "#10B981", group: "utility",
    describe: "Storm collectors routed by the real Centenary Park retention basin (3,785 m³, 3° gradient, Landprocess 2017). Overflow → BMA mains under Rama IV." },
  // Energy
  { id: "campus-energy", label: "Energy heatmap (model)",  swatch: "#1F6E43", group: "utility",
    describe: "Recolor the 339 campus building footprints by current kWh draw. Cool teal = idle, yellow = peak, red = anomaly (stuck cooling, after-hours load, possible outage). Synthetic model derived from building geometry + hour-of-day until BEMS is wired." },
  // Trees + canopy
  { id: "campus-trees", label: "Trees (4,107 documented)",  swatch: "#2A9859", group: "environment",
    describe: "Every documented tree on the campus bbox — 4,107 individual trees from OSM, each rendered as a canopy disk at its real crown radius. Compare against the satellite to verify or flag missing coverage. Placeholder until PMCU shares the full inventory with species + age + health." },
  { id: "campus-wood", label: "Grove polygons",  swatch: "#2A9859", group: "environment",
    describe: "67 contiguous wood / grove areas inside the campus bbox — soft green fills under individual trees so the canopy reads as a coherent area, not a pointillist scatter." },
  // ── Pratumwan X-Ray ──────────────────────────────────────────────────────
  { id: "pratumwan-boundary", label: "District outline", swatch: "#FBBF24", group: "xray",
    describe: "Pratumwan (Pathum Wan) administrative outline — BMA district shape, 8.37 km², bordered by Khlong Saen Saeb (N), Rama IV (S), Phadung Krung Kasem (W), Wireless Rd (E)." },
  { id: "pratumwan-ground-plane", label: "Ground plane (X-Ray)", swatch: "#0C0A06", group: "xray",
    describe: "Translucent ground overlay — fades street-level surface so subsurface utilities, tunnels, and substations read clearly through it. Only active when X-Ray lens is on." },
  { id: "pratumwan-metro-tunnels", label: "MRT Blue Line (schematic)", swatch: "#506478", group: "xray",
    describe: "Known MRT Blue Line alignment under Rama IV — simplified hairline trace, not TBM-scale geometry." },
  { id: "pratumwan-metro-stations", label: "MRT stations (schematic)", swatch: "#506478", group: "xray",
    describe: "Station footprints at Sam Yan / Hua Lamphong / Si Lom — flat outlines, not 3D cut-and-cover boxes." },
  { id: "pratumwan-water", label: "Water mains (MWA · schematic)", swatch: "#3878a0", group: "xray",
    describe: "Approximate MWA trunk routing under major arterials — schematic only until live GIS is connected." },
  { id: "pratumwan-sewer", label: "Sewers (BMA · schematic)", swatch: "#6f645a", group: "xray",
    describe: "Approximate combined sewer routing toward Chong Nonsi — schematic only." },
  { id: "pratumwan-storm", label: "Storm / khlongs (schematic)", swatch: "#0e7490", group: "xray",
    describe: "Open khlongs + storm culverts — simplified traces to Khlong Saen Saeb and Phadung Krung Kasem." },
  { id: "pratumwan-mea-hv", label: "MEA HV (approx · optional)", swatch: "#a16207", group: "xray",
    describe: "Approximate MEA HV cable routing — optional layer, off by default for clarity." },
];

// ── Map legend metadata ────────────────────────────────────────────────────
// Plain-language labels for the on-map legend (MapLegend). Written for a
// non-technical viewer: what the line/fill/dot IS, not the layer's internal
// name. `kind` picks the swatch shape so it matches the layer's actual
// visual — a line layer gets a line, a filled polygon gets a fill chip, a
// scatter layer gets a dot, a heatmap gets a gradient bar.
export type LegendKind = "line" | "fill" | "point" | "heat";

export interface LayerLegendMeta {
  /** Plain-language label, e.g. "Metropolitan Water Authority mains". */
  label: string;
  kind: LegendKind;
}

export const LAYER_LEGEND: Record<LayerId, LayerLegendMeta> = {
  // Campus
  "campus-boundary":       { label: "Campus perimeter",                          kind: "fill" },
  "campus-buildings":      { label: "Campus buildings",                          kind: "fill" },
  "bma-aq-stations":       { label: "Air-quality stations (PM2.5)",              kind: "point" },
  "campus-gates":          { label: "Campus gates & entrances",                  kind: "point" },
  // Mobility
  "cu-shuttle-routes":     { label: "CU electric shuttle — all 5 routes",        kind: "line" },
  "cu-shuttle-1":          { label: "CU shuttle — Line 1 (Saturday)",            kind: "line" },
  "cu-shuttle-2":          { label: "CU shuttle — Line 2 (Saturday)",            kind: "line" },
  "cu-shuttle-3":          { label: "CU shuttle — Line 3 (weekday)",             kind: "line" },
  "cu-shuttle-4":          { label: "CU shuttle — Line 4 (weekday)",             kind: "line" },
  "cu-shuttle-5":          { label: "CU shuttle — Line 5 (weekday)",             kind: "line" },
  "cu-shuttle-stops":      { label: "CU shuttle stops",                          kind: "point" },
  "cu-shuttle-vehicles":   { label: "CU shuttle buses (simulated)",             kind: "point" },
  "transit-stations":      { label: "BTS / MRT stations",                        kind: "point" },
  "transit-lines":         { label: "BTS / MRT rail lines",                      kind: "line" },
  "traffic-live":          { label: "Live road speed — green flow, red congestion", kind: "line" },
  "cctv-cameras":          { label: "Traffic CCTV cameras",                      kind: "point" },
  // Operations (modeled)
  "parking-capacity":      { label: "Parking lots — occupied / capacity",       kind: "fill" },
  "building-occupancy":    { label: "People in building (modeled)",             kind: "fill" },
  "internal-traffic":      { label: "Campus road speed (modeled)",              kind: "line" },
  "wifi-signal":           { label: "WiFi signal strength & access points",     kind: "heat" },
  "fire-hose":             { label: "Fire hose cabinets & hydrants",            kind: "point" },
  "trash-collection":      { label: "Trash & recycling bins — fill level",      kind: "point" },
  // Incidents
  "incidents-itic":        { label: "Live traffic incidents (iTIC)",             kind: "point" },
  "incidents-city-reports":{ label: "Citizen reports (LINE · Telegram · Traffy)", kind: "point" },
  "incidents-city-heatmap":{ label: "Citizen-report hotspots",                   kind: "heat" },
  // Environment / risk
  "flood-gauges":          { label: "Live flood & rain gauges",                  kind: "point" },
  "campus-trees":          { label: "Documented trees (4,107)",                  kind: "point" },
  "campus-wood":           { label: "Groves & canopy areas",                     kind: "fill" },
  // Utilities + energy
  "utility-electricity":   { label: "Power lines & substations (MEA)",           kind: "line" },
  "utility-water":         { label: "Water mains (MWA)",                         kind: "line" },
  "utility-drainage":      { label: "Storm drains & retention basin",            kind: "line" },
  "campus-energy":         { label: "Building energy use (kWh model)",           kind: "fill" },
  // Pratumwan X-Ray
  "pratumwan-boundary":    { label: "Pathum Wan district outline",               kind: "line" },
  "pratumwan-ground-plane":{ label: "Faded ground plane (X-Ray view)",           kind: "fill" },
  "pratumwan-metro-tunnels":{ label: "MRT Blue Line tunnel (underground)",       kind: "line" },
  "pratumwan-metro-stations":{ label: "MRT station footprints",                  kind: "fill" },
  "pratumwan-water":       { label: "Metropolitan Water Authority mains",        kind: "line" },
  "pratumwan-sewer":       { label: "BMA sewers",                                kind: "line" },
  "pratumwan-storm":       { label: "Storm drains & khlongs",                    kind: "line" },
  "pratumwan-mea-hv":      { label: "MEA high-voltage cables (underground)",     kind: "line" },
};
